import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, Plus, TrendingUp, Info, Lock, Globe, Calendar, LogIn, Check, Users, Swords, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RankingLists() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [joinedListIds, setJoinedListIds] = useState([]);

  useEffect(() => {
    checkAuthAndLoadLists();
  }, []);

  const checkAuthAndLoadLists = async () => {
    let authenticated = false;
    try {
      // Check if user is authenticated
      await axios.get(`${API}/auth/me`, { withCredentials: true });
      authenticated = true;
      setIsAuthenticated(true);
      
      // Load user's joined rankings
      const myRankingsRes = await axios.get(`${API}/users/my-rankings`, { withCredentials: true });
      setJoinedListIds(myRankingsRes.data.map(r => r.list_id));
    } catch (error) {
      setIsAuthenticated(false);
      setJoinedListIds([]);
    }
    
    // Load lists regardless of auth status
    try {
      const response = await axios.get(`${API}/ranking-lists`, { withCredentials: true });
      setLists(response.data);
    } catch (error) {
      toast.error('Failed to load ranking lists');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinList = async (listId) => {
    if (!isAuthenticated) {
      toast.error('Please login to join a ranking list');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/ranking-lists/${listId}/join`, {}, { withCredentials: true });
      toast.success('Successfully joined ranking list!');
      setJoinedListIds([...joinedListIds, listId]);
      navigate(`/ranking-lists/${listId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join list');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading ranking lists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isAuthenticated ? t('backToDashboard') : t('backToHome')}
          </Button>
          <div className="flex items-center gap-3">
            {!isAuthenticated && (
              <Button 
                onClick={() => navigate('/login')}
                className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full"
              >
                <LogIn className="w-4 h-4 mr-2" />
                {t('login')}
              </Button>
            )}
            <Logo size="lg" />
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-semibold mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t('rankingLists')}
          </h1>
          <p className="text-gray-400">
            {isAuthenticated 
              ? t('browseAndJoin')
              : t('viewPublicLists')}
          </p>
        </div>

        {lists.length === 0 ? (
          <Card className="bg-[#111111] border-[#27272A]">
            <CardContent className="py-12 text-center">
              <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No ranking lists available yet</p>
              <p className="text-sm text-gray-500">Ask an admin to create one!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {lists.map((list, index) => (
              <Card 
                key={list.list_id} 
                className={`bg-[#111111] border-[#27272A] hover:border-[#007AFF]/50 cursor-pointer card-hover animate-slide-up ${list.is_locked ? 'opacity-75' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/ranking-lists/${list.list_id}`)}
                data-testid="ranking-list-card"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-white text-lg sm:text-xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {list.name}
                        </CardTitle>
                        {list.is_locked && (
                          <span className="flex items-center gap-1 text-xs bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-1 rounded-full">
                            <Lock className="w-3 h-3" />
                          </span>
                        )}
                        {list.is_public && (
                          <span className="flex items-center gap-1 text-xs bg-[#007AFF]/20 text-[#007AFF] px-2 py-1 rounded-full">
                            <Globe className="w-3 h-3" />
                          </span>
                        )}
                        {list.club_name && (
                          <span className="flex items-center gap-1 text-xs bg-[#34C759]/20 text-[#34C759] px-2 py-1 rounded-full">
                            <Building2 className="w-3 h-3" />
                            {list.club_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-[#007AFF]/20 text-[#007AFF] rounded-full">
                          {list.match_format}
                        </Badge>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {list.player_count || 0} {t('players')}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Swords className="w-3 h-3" />
                          {list.match_count || 0} {t('matches')}
                        </span>
                        {list.ends_at && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Until {new Date(list.ends_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <TrendingUp className="w-8 h-8 text-[#007AFF]" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button 
                      data-testid="view-leaderboard-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/ranking-lists/${list.list_id}`);
                      }}
                      className="flex-1 bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold"
                    >
                      View Leaderboard
                    </Button>
                    <Button 
                      data-testid="info-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedList(list);
                        setIsInfoOpen(true);
                      }}
                      variant="outline"
                      className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                    >
                      <Info className="w-4 h-4" />
                    </Button>
                    {!list.is_locked && isAuthenticated && (
                      joinedListIds.includes(list.list_id) ? (
                        <Button 
                          data-testid="joined-list-btn"
                          variant="outline"
                          disabled
                          className="border-green-600 text-green-500 rounded-full cursor-default"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t('joined')}
                        </Button>
                      ) : (
                        <Button 
                          data-testid="join-list-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoinList(list.list_id);
                          }}
                          variant="outline"
                          className="border-[#27272A] hover:bg-white/10 text-white rounded-full"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('join')}
                        </Button>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Dialog */}
        <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
          <DialogContent className="bg-[#111111] border-[#27272A] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                <Info className="w-5 h-5 text-[#007AFF]" />
                {selectedList?.name}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Ranking List Information
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Match Format</p>
                <p className="text-white font-semibold">{selectedList?.match_format}</p>
              </div>
              {selectedList?.ends_at && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">Running Until</p>
                  <p className="text-white">{new Date(selectedList.ends_at).toLocaleDateString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-400 mb-1">Description</p>
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                  <p className="text-white whitespace-pre-wrap">{selectedList?.description || 'No description provided.'}</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default RankingLists;